import React, { useState } from 'react';
import { Header } from '@/components/Header';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useLanguage } from '@/context/LanguageContext';
import { HelpCircle, ChevronDown, ChevronUp } from 'lucide-react';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Link } from 'react-router-dom';
import { Seo } from '@/components/Seo';
import { Helmet } from '@dr.pogodin/react-helmet';

/** Convert a React node or string answer into a plain-text string for JSON-LD.
 *  React elements -> recursively flatten children. Strips out markup. */
const answerToText = (node) => {
  if (node == null || typeof node === 'boolean') return '';
  if (typeof node === 'string' || typeof node === 'number') return String(node);
  if (Array.isArray(node)) return node.map(answerToText).join('');
  if (node.props && node.props.children) return answerToText(node.props.children);
  return '';
};

const FAQItem = ({ question, answer }) => {
  const [isOpen, setIsOpen] = useState(false);
  
  // Function to render answer with bullet points and links
  const renderAnswer = (content) => {
    // If content is a React element, render directly
    if (React.isValidElement(content)) return content;
    const text = content;
    // Check if answer contains bullet points
    if (text.includes('• **')) {
      const parts = text.split('\n\n');
      return (
        <div className="space-y-3">
          {parts.map((part, idx) => {
            if (part.startsWith('• **')) {
              // Parse bullet point with bold label
              const match = part.match(/• \*\*(.+?)\*\* — (.+)/);
              if (match) {
                return (
                  <div key={idx} className="flex items-start gap-2">
                    <span className="text-primary mt-1">•</span>
                    <p className="text-muted-foreground leading-relaxed">
                      <strong className="text-foreground">{match[1]}</strong> — {match[2]}
                    </p>
                  </div>
                );
              }
            }
            return (
              <p key={idx} className="text-muted-foreground leading-relaxed">
                {part}
              </p>
            );
          })}
        </div>
      );
    }
    return <p className="text-muted-foreground leading-relaxed">{text}</p>;
  };
  
  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <Card className="border-l-4 border-l-primary/50">
        <CollapsibleTrigger className="w-full">
          <CardHeader className="pb-2 cursor-pointer hover:bg-muted/30 transition-colors">
            <CardTitle className="text-lg font-semibold text-foreground flex items-center justify-between">
              <span className="text-left">{question}</span>
              {isOpen ? (
                <ChevronUp className="h-5 w-5 text-muted-foreground flex-shrink-0 ml-2" />
              ) : (
                <ChevronDown className="h-5 w-5 text-muted-foreground flex-shrink-0 ml-2" />
              )}
            </CardTitle>
          </CardHeader>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <CardContent className="pt-0">
            {renderAnswer(answer)}
          </CardContent>
        </CollapsibleContent>
      </Card>
    </Collapsible>
  );
};

const FAQsPage = () => {
  const { t, isSpanish } = useLanguage();
  
  const faqs = [
    // Download / install links
    {
      question: isSpanish ? '¿Dónde puedo descargar la aplicación?' : 'Where can I download the app?',
      answer: isSpanish ? (
        <div className="space-y-2">
          <p className="text-muted-foreground leading-relaxed">
            Puedes usar ClearCommunicationApp directamente en tu navegador — o descargarla desde la tienda de aplicaciones de tu dispositivo:
          </p>
          <p className="text-muted-foreground leading-relaxed">
            <strong className="text-foreground">Google Play (Android):</strong>{' '}
            <a href="https://play.google.com/store/apps/details?id=www.ClearCommunicationApp.com" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline font-medium break-all" data-testid="faq-play-store-link">
              https://play.google.com/store/apps/details?id=www.ClearCommunicationApp.com
            </a>
          </p>
          <p className="text-muted-foreground leading-relaxed">
            <strong className="text-foreground">Amazon Appstore (tabletas Fire y Fire TV):</strong>{' '}
            <a href="https://www.amazon.com/gp/product/B0GX2ZJQGV" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline font-medium break-all" data-testid="faq-amazon-store-link">
              https://www.amazon.com/gp/product/B0GX2ZJQGV
            </a>
          </p>
          <p className="text-muted-foreground leading-relaxed">
            En iPhone/iPad o computadora, toca el botón <strong className="text-foreground">Install</strong> en la parte superior de la página para agregar la app a tu pantalla de inicio.
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          <p className="text-muted-foreground leading-relaxed">
            You can use ClearCommunicationApp right here in your browser — or grab it from your device's app store:
          </p>
          <p className="text-muted-foreground leading-relaxed">
            <strong className="text-foreground">Google Play (Android):</strong>{' '}
            <a href="https://play.google.com/store/apps/details?id=www.ClearCommunicationApp.com" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline font-medium break-all" data-testid="faq-play-store-link">
              https://play.google.com/store/apps/details?id=www.ClearCommunicationApp.com
            </a>
          </p>
          <p className="text-muted-foreground leading-relaxed">
            <strong className="text-foreground">Amazon Appstore (Fire tablets & Fire TV):</strong>{' '}
            <a href="https://www.amazon.com/gp/product/B0GX2ZJQGV" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline font-medium break-all" data-testid="faq-amazon-store-link">
              https://www.amazon.com/gp/product/B0GX2ZJQGV
            </a>
          </p>
          <p className="text-muted-foreground leading-relaxed">
            On iPhone/iPad or desktop, tap the <strong className="text-foreground">Install</strong> button at the top of the page to add the app to your home screen.
          </p>
        </div>
      )
    },
    // Section Breakdown FAQ
    {
      question: t('faqSectionBreakdown'),
      answer: t('faqSectionBreakdownAnswer')
    },
    // Subscription FAQs
    {
      question: isSpanish ? "¿Cuál es la diferencia entre el Acceso Gratis Diario y la Prueba Gratis VIP?" : "What's the difference between Daily Free Access and the VIP Free Trial?",
      answer: isSpanish 
        ? "El Acceso Gratis Diario te permite ver 2 frases cada día sin registrarte—se reinicia a medianoche. La Prueba Gratis de 7 Días VIP es parte de la suscripción VIP y te da acceso ilimitado a todas las más de 3,400 frases por 7 días, después de lo cual se te cobrará $1.99/mes si no cancelas."
        : "Daily Free Access lets you browse all phrases without signing up. The 7-Day VIP Free Trial removes all ads and gives you a clean, distraction-free experience for 7 days, after which you'll be charged $1.99/month if you don't cancel."
    },
    {
      question: isSpanish ? "¿Cuándo se me cobrará?" : "When will I be charged?",
      answer: isSpanish 
        ? "Si inicias la prueba gratuita, se te cobrará $1.99 el día 8. Si cancelas antes del día 8, no se te cobrará nada."
        : "If you start the free trial, you'll be charged $1.99 on day 8. If you cancel before day 8, you won't be charged anything."
    },
    {
      question: isSpanish ? "¿Puedo cancelar en cualquier momento?" : "Can I cancel anytime?",
      answer: isSpanish 
        ? "¡Sí! Puedes cancelar tu suscripción en cualquier momento en línea a través de la página de Suscripción, o enviándonos un correo a Support@ClearCommunicationApp.com. Si cancelas durante la prueba, no se te cobrará. Si cancelas después de que se te cobre, mantendrás acceso hasta el final de tu período de facturación."
        : "Yes! You can cancel your subscription at any time online through the Subscription page, or by emailing us at Support@ClearCommunicationApp.com. If you cancel during the trial, you won't be charged. If you cancel after being charged, you'll keep access until the end of your billing period."
    },
    {
      question: isSpanish ? "¿Qué métodos de pago aceptan?" : "What payment methods do you accept?",
      answer: isSpanish 
        ? "Aceptamos todas las tarjetas de crédito principales, Google Pay, Apple Pay, y Link a través de nuestro procesador de pagos seguro Stripe."
        : "We accept all major credit cards, Google Pay, Apple Pay, and Link through our secure Stripe payment processor."
    },
    // App-specific FAQs
    {
      question: t('faqWhyBuilt'),
      answer: t('faqWhyBuiltAnswer')
    },
    {
      question: isSpanish ? "¿Cómo funciona el cambio de idioma?" : "How does the language toggle work?",
      answer: isSpanish 
        ? "El interruptor EN/ES en la parte superior de la página traduce todas las definiciones, significados, ejemplos y consejos entre inglés y español. Las frases en sí permanecen en su idioma original para mantener la autenticidad." 
        : "The EN/ES toggle at the top of the page translates all definitions, meanings, examples, and tips between English and Spanish. The phrases themselves remain in their original language to maintain authenticity."
    },
    {
      question: isSpanish ? "¿Puedo solicitar que se agreguen nuevas frases?" : "Can I request new phrases to be added?",
      answer: isSpanish 
        ? "¡Absolutamente! Usa la página de Comentarios / Solicitar Frase para enviar frases que te gustaría ver en la aplicación. Revisamos todas las solicitudes y agregamos frases regularmente." 
        : "Absolutely! Use the Feedback / Request Phrase page to submit phrases you'd like to see in the app. We review all requests and add phrases regularly."
    },
    {
      question: isSpanish ? "¿Cuál es la diferencia entre 'Tesauro de Frases' y 'Frases Relacionadas'?" : "What's the difference between 'Phrase Thesaurus' and 'Related Phrases'?",
      answer: isSpanish 
        ? "'Tesauro de Frases' muestra sinónimos y palabras similares que NO están en nuestra biblioteca - son solo para referencia. 'Frases Relacionadas' son enlaces clickeables a otras frases que SÍ existen en nuestra colección, para que puedas explorar términos conectados." 
        : "'Phrase Thesaurus' shows synonyms and similar words that are NOT in our library - they're just for reference. 'Related Phrases' are clickable links to other phrases that DO exist in our collection, so you can explore connected terms."
    },
    {
      question: isSpanish ? "¿Funciona la aplicación sin conexión?" : "Does the app work offline?",
      answer: isSpanish 
        ? "Actualmente, se requiere una conexión a internet para usar la aplicación. ¡Estamos trabajando en capacidades sin conexión para futuras versiones!" 
        : "Currently, an internet connection is required to use the app. We're working on offline capabilities for future versions!"
    },
    // Privacy & Account
    {
      question: isSpanish ? "¿Dónde puedo leer la Política de Privacidad?" : "Where can I read the Privacy Policy?",
      answer: isSpanish ? (
        <p className="text-muted-foreground leading-relaxed">
          Puedes leer nuestra Política de Privacidad completa aquí: <Link to="/privacy" className="text-primary hover:underline font-medium">Política de Privacidad</Link>. Explica qué datos recopilamos, cómo los usamos y tus derechos.
        </p>
      ) : (
        <p className="text-muted-foreground leading-relaxed">
          You can read our full Privacy Policy here: <Link to="/privacy" className="text-primary hover:underline font-medium">Privacy Policy</Link>. It explains what data we collect, how we use it, and your rights.
        </p>
      )
    },
    {
      question: isSpanish ? "¿Cómo elimino mi cuenta y mis datos?" : "How do I delete my account and data?",
      answer: isSpanish ? (
        <div className="space-y-2">
          <p className="text-muted-foreground leading-relaxed">
            Puedes solicitar la eliminación permanente de tu cuenta y todos los datos asociados visitando nuestra <Link to="/privacy#data-deletion" className="text-primary hover:underline font-medium">página de Eliminación de Cuenta</Link>.
          </p>
          <p className="text-muted-foreground leading-relaxed">
            Ingresa tu correo electrónico, escribe "delete my data" para confirmar, y envía la solicitud. Tu cuenta y todos los datos asociados serán eliminados permanentemente dentro de 30 días.
          </p>
          <p className="text-muted-foreground leading-relaxed">
            También puedes contactarnos en <a href="mailto:Support@ClearCommunicationApp.com" className="text-primary hover:underline font-medium">Support@ClearCommunicationApp.com</a>.
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          <p className="text-muted-foreground leading-relaxed">
            You can request permanent deletion of your account and all associated data by visiting our <Link to="/privacy#data-deletion" className="text-primary hover:underline font-medium">Account Deletion page</Link>.
          </p>
          <p className="text-muted-foreground leading-relaxed">
            Enter your email, type "delete my data" to confirm, and submit. Your account and all associated data will be permanently deleted within 30 days.
          </p>
          <p className="text-muted-foreground leading-relaxed">
            You can also contact us at <a href="mailto:Support@ClearCommunicationApp.com" className="text-primary hover:underline font-medium">Support@ClearCommunicationApp.com</a>.
          </p>
        </div>
      )
    },
    {
      question: isSpanish ? "¿Dónde puedo leer los Estándares de Seguridad (CSAE)?" : "Where can I read the Safety Standards (CSAE)?",
      answer: isSpanish ? (
        <p className="text-muted-foreground leading-relaxed">
          Nuestros estándares completos contra el abuso y la explotación sexual infantil están publicados aquí: <Link to="/safety" className="text-primary hover:underline font-medium">Estándares de Seguridad</Link>. Detallan nuestras políticas de tolerancia cero, mecanismos de denuncia y medidas de prevención.
        </p>
      ) : (
        <p className="text-muted-foreground leading-relaxed">
          Our full standards against Child Sexual Abuse and Exploitation (CSAE) are published here: <Link to="/safety" className="text-primary hover:underline font-medium">Safety Standards</Link>. They detail our zero-tolerance policies, reporting mechanisms, and prevention measures.
        </p>
      )
    }
  ];
  
  return (
    <div className="min-h-screen bg-background">
      <Seo
        path="/faqs"
        title="FAQs — ClearCommunicationApp"
        description="Answers to common questions about subscriptions, accounts, language toggle, accessibility, and how the phrase library works."
      />
      {/* FAQPage JSON-LD — Google may show top FAQ entries as expandable
          rich results directly in search, dominating SERP real estate. */}
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: faqs.slice(0, 10).map((f) => ({
              "@type": "Question",
              name: typeof f.question === 'string' ? f.question : answerToText(f.question),
              acceptedAnswer: {
                "@type": "Answer",
                text: answerToText(f.answer).trim() || (typeof f.question === 'string' ? f.question : ''),
              },
            })),
          })}
        </script>
      </Helmet>
      <Header />
      
      <section className="py-12 sm:py-16">
        <div className="container max-w-3xl">
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center gap-1.5 mb-4">
              <HelpCircle className="h-8 w-8 text-primary" />
              <h1 className="text-3xl sm:text-4xl font-bold">
                <span className="bg-gradient-to-r from-[#00E5CC] to-[#7C3AED] bg-clip-text text-transparent">
                  {t('faqTitle')}
                </span>
              </h1>
            </div>
            <p className="text-muted-foreground">
              {isSpanish 
                ? 'Toca una pregunta para expandir la respuesta' 
                : 'Tap a question to expand the answer'}
            </p>
          </div>
          
          <div className="space-y-3">
            {faqs.map((faq, index) => (
              <FAQItem key={index} question={faq.question} answer={faq.answer} />
            ))}
          </div>
          
          <div className="mt-8 text-center">
            <p className="text-muted-foreground">
              {isSpanish 
                ? '¿Tienes más preguntas? ' 
                : 'Have more questions? '}
              <a href="/feedback" className="text-primary hover:underline" data-testid="faq-feedback-link">
                {isSpanish ? 'Comparte comentarios o solicitudes' : 'Share feedback or submit a request'}
              </a>
              {' · '}
              <a href="/about" className="text-primary hover:underline" data-testid="faq-about-link">
                {isSpanish ? 'Acerca de la app' : 'About the app'}
              </a>
            </p>
            {/* Legal links + social icons are rendered globally by SiteFooter;
                removed inline duplicates here per UX feedback. */}
          </div>
        </div>
      </section>
    </div>
  );
};

export default FAQsPage;
